import { push } from 'connected-react-router';

import { getPathWithReplacedProjectId } from 'util/getPathWithReplacedProjectId';
import { removeQueryParams } from 'util/removeQueryParams';
import { selectLocationPath, selectQueryString } from 'util/selector/routerSelector';
import {
  selectLanguage,
  selectLegalFiltersWithModeQuery,
  selectLegalFiltersWithModeQueryAsObject,
} from 'util/selector/querySelector';

import { AppRoute } from 'app/route/app';
import { compilePath, CompilePathParams, CompilePathQuery } from 'router/compilePath';
import flow from 'lodash/flow';
import { ProjectModel } from 'model/Project.model';
import { ThunkAction } from '@reduxjs/toolkit';
import { AppState } from 'reduxStore/appStore';
import { Action } from 'redux';
import qs from 'qs';
import { resetSelectedClassifications } from 'reduxStore/filtering/slice';
import { resetSelectedDepartments } from 'reduxStore/departments/slice';
import { clearProjectKindAndSettings } from 'reduxStore/project/slice';
import { resetHeadlinesDepartments } from 'reduxStore/headlines/slice';

export type RouteParams = {
  pattern: string;
  params?: CompilePathParams;
  query?: CompilePathQuery;
};

/**
 * Push new route to the history
 */
export const route = ({
  pattern,
  params,
  query,
}: RouteParams): ThunkAction<void, AppState, void, Action<string>> => (dispatch) =>
  dispatch(push(compilePath(pattern, params, query)));

export type RouteWithQueryParams = {
  pattern: string;
  params?: CompilePathParams;
  extraQuery?: CompilePathQuery;
};

export const routeWithQuery = ({
  pattern,
  params,
  extraQuery,
}: RouteWithQueryParams): ThunkAction<void, AppState, void, Action<string>> => (
  dispatch,
  getState
) => {
  const currentQuery = selectQueryString(getState());
  const mergedQuery = extraQuery
    ? qs.stringify({ ...qs.parse(currentQuery), ...extraQuery }, { encode: false })
    : currentQuery;
  dispatch(push(compilePath(pattern, params) + '?' + mergedQuery));
};

export const pushQuery = ({
  query,
}: {
  query: string;
}): ThunkAction<void, AppState, void, Action<string>> => (dispatch, getState) =>
  dispatch(push(selectLocationPath(getState()) + '?' + query));

export const updateQuery = ({
  query,
}: {
  query: Record<string, unknown>;
}): ThunkAction<void, AppState, void, Action<string>> => (dispatch, getState) => {
  const updatedQueryParams = { ...selectLegalFiltersWithModeQueryAsObject(getState()), ...query };
  const updatedQuery = qs.stringify(updatedQueryParams, { encode: false });
  return dispatch(push(selectLocationPath(getState()) + '?' + updatedQuery));
};

export const goToPage = (url: string) => () => {
  window.location.href = url;
};

export const openInNewTab = (url: string) => () => {
  window.open(url, '_blank');
};

export type ChangeProjectParams = {
  projectId: ProjectModel['id'];
  navigateToSummary?: boolean;
};

export const changeProject = ({
  projectId,
  navigateToSummary = false,
}: ChangeProjectParams): ThunkAction<void, AppState, void, Action<string>> => (
  dispatch,
  getState
) => {
  let newLocation: string;
  const language = selectLanguage(getState());
  const query = language ? { language } : undefined;

  if (navigateToSummary) {
    newLocation = compilePath(AppRoute.PROJECT_PATTERN, { id: projectId }, query);
  } else {
    const location = selectLocationPath(getState()) || '';
    const pathWithReplacedProjectId = flow(
      getPathWithReplacedProjectId(projectId),
      removeQueryParams
    )(location);
    newLocation = query
      ? `${pathWithReplacedProjectId}?${qs.stringify(query, { encode: false })}`
      : pathWithReplacedProjectId;
  }

  dispatch(clearProjectKindAndSettings());
  dispatch(resetSelectedClassifications());
  dispatch(resetSelectedDepartments());
  dispatch(resetHeadlinesDepartments());

  if (!newLocation) {
    console.error('changeProjectId called from invalid location');
    return;
  }

  dispatch(push(newLocation));
};

// Legal query is the query that only has elements that user's role specifies
export const pushLegalQuery = (): ThunkAction<void, AppState, void, Action<string>> => (
  dispatch,
  getState
) => {
  const query = selectLegalFiltersWithModeQuery(getState());

  dispatch(pushQuery({ query }));
};
